import { useCallback } from 'react';
import type {
  OnDrag,
  OnDragStart,
  OnResize,
  OnResizeStart,
  OnRotate,
  OnRotateStart,
} from 'react-moveable';

import type { SlideElementUpdate } from './useSlideElements';

import type { SlideElement } from '@/shared/types/slide';
import { MIN_ELEMENT_SIZE, SLIDE_PAGE_WIDTH, SLIDE_PAGE_HEIGHT } from '@/shared/types/slide';
import type { ResolvedSlideElement } from '@/shared/theming/resolve';

// ---------------------------------------------------------------------------
// Hook: Moveable event handlers for slide element drag / resize / rotate.
//
// Pattern (same as mockup, bugs fixed):
//   • During interaction → live DOM style updates for smoothness.
//   • On interaction end → commit rounded + clamped values to state.
//
// Divergences from the mockup (`Slides Editor/src/hooks/useMoveable.ts`):
//   • Boundary clamping handles elements wider/taller than canvas.
//   • Rotation normalised to [0, 360).
//   • Minimum element size enforced at 20 px (not 10).
//   • No `flushSync` — updates go through the normal React commit path
//     via the `onUpdate` callback which is already batched by React 18+.
// ---------------------------------------------------------------------------

interface UseMoveableOptions {
  onUpdateMany?: (updates: SlideElementUpdate[]) => void;
}

export function useMoveable(
  // The canvas passes the RESOLVED element list, so geometry is always concrete.
  elements: ResolvedSlideElement[],
  onUpdate: (id: string, patch: Partial<SlideElement>) => void,
  { onUpdateMany }: UseMoveableOptions = {}
) {
  const getElement = useCallback((id: string) => elements.find((el) => el.id === id), [elements]);

  /** Clamp so the element can't fully leave the canvas on any side.
   *  At least a sliver of the element always remains visible. */
  const clampXY = (x: number, y: number, w: number, h: number) => ({
    x: Math.round(Math.max(-w, Math.min(SLIDE_PAGE_WIDTH, x))),
    y: Math.round(Math.max(-h, Math.min(SLIDE_PAGE_HEIGHT, y))),
  });

  // -- Drag -----------------------------------------------------------------

  const onDragStart = useCallback(
    (id: string) => (_e: OnDragStart) => {
      if (!getElement(id)) return false;
    },
    [getElement]
  );

  const onDrag = useCallback(
    (id: string) => (e: OnDrag) => {
      const el = getElement(id);
      if (!el) return;
      const { x, y } = clampXY(e.left, e.top, el.position.width, el.position.height);
      e.target.style.left = `${x}px`;
      e.target.style.top = `${y}px`;
    },
    [getElement]
  );

  const onDragEnd = useCallback(
    (id: string) => (e: { lastEvent: OnDrag | null; isDrag: boolean }) => {
      const el = getElement(id);
      if (!el || !e.lastEvent) return;
      const { x, y } = clampXY(
        e.lastEvent.left,
        e.lastEvent.top,
        el.position.width,
        el.position.height
      );
      onUpdate(id, { position: { ...el.position, x, y } });
    },
    [getElement, onUpdate]
  );

  // -- Resize ---------------------------------------------------------------

  const onResizeStart = useCallback(
    (id: string) => (e: OnResizeStart) => {
      if (!getElement(id)) return false;
      e.setMin([MIN_ELEMENT_SIZE, MIN_ELEMENT_SIZE]);
    },
    [getElement]
  );

  const onResize = useCallback(
    (_id: string) => (e: OnResize) => {
      const w = Math.max(MIN_ELEMENT_SIZE, e.width);
      const h = Math.max(MIN_ELEMENT_SIZE, e.height);
      const { x, y } = clampXY(e.drag.left, e.drag.top, w, h);
      e.target.style.width = `${w}px`;
      e.target.style.height = `${h}px`;
      e.target.style.left = `${x}px`;
      e.target.style.top = `${y}px`;
    },
    []
  );

  const onResizeEnd = useCallback(
    (id: string) => (e: { lastEvent: OnResize | null; isDrag: boolean }) => {
      const el = getElement(id);
      if (!el || !e.lastEvent) return;
      const width = Math.max(MIN_ELEMENT_SIZE, Math.round(e.lastEvent.width));
      const height = Math.max(MIN_ELEMENT_SIZE, Math.round(e.lastEvent.height));
      const { x, y } = clampXY(e.lastEvent.drag.left, e.lastEvent.drag.top, width, height);
      onUpdate(id, { position: { ...el.position, x, y, width, height } });
    },
    [getElement, onUpdate]
  );

  // -- Rotate ---------------------------------------------------------------

  const normalizeRotation = (deg: number) => ((Math.round(deg) % 360) + 360) % 360;

  const onRotateStart = useCallback(
    (id: string) => (e: OnRotateStart) => {
      const el = getElement(id);
      if (!el) return false;
      e.set(el.position.rotation ?? 0);
    },
    [getElement]
  );

  const onRotate = useCallback(
    (_id: string) => (e: OnRotate) => {
      e.target.style.transform = `rotate(${e.rotate}deg)`;
    },
    []
  );

  const onRotateEnd = useCallback(
    (id: string) => (e: { lastEvent: OnRotate | null; isDrag: boolean }) => {
      const el = getElement(id);
      if (!el || !e.lastEvent) return;
      onUpdate(id, {
        position: { ...el.position, rotation: normalizeRotation(e.lastEvent.rotate) },
      });
    },
    [getElement, onUpdate]
  );

  // -- Public API -----------------------------------------------------------

  const getHandlers = useCallback(
    (id: string) => ({
      onDragStart: onDragStart(id),
      onDrag: onDrag(id),
      onDragEnd: onDragEnd(id),
      onResizeStart: onResizeStart(id),
      onResize: onResize(id),
      onResizeEnd: onResizeEnd(id),
      onRotateStart: onRotateStart(id),
      onRotate: onRotate(id),
      onRotateEnd: onRotateEnd(id),
    }),
    [
      onDragStart,
      onDrag,
      onDragEnd,
      onResizeStart,
      onResize,
      onResizeEnd,
      onRotateStart,
      onRotate,
      onRotateEnd,
    ]
  );

  // -- Keyboard arrow key nudging -------------------------------------------

  const nudge = useCallback(
    (selectedIds: string[], dx: number, dy: number) => {
      const items: Array<{ id: string; el: ResolvedSlideElement }> = [];
      for (const id of selectedIds) {
        const el = getElement(id);
        if (!el || el.locked) continue;
        items.push({ id, el });
      }
      if (items.length === 0) return;

      if (items.length === 1) {
        const { id, el } = items[0];
        const { x, y } = clampXY(
          el.position.x + dx,
          el.position.y + dy,
          el.position.width,
          el.position.height
        );
        onUpdate(id, { position: { ...el.position, x, y } });
        return;
      }

      // Multi-element: clamp as a group so relative positions are preserved
      let maxX = -Infinity,
        maxY = -Infinity,
        minRight = Infinity,
        minBottom = Infinity;
      for (const { el } of items) {
        const nx = el.position.x + dx,
          ny = el.position.y + dy;
        maxX = Math.max(maxX, nx);
        maxY = Math.max(maxY, ny);
        minRight = Math.min(minRight, nx + el.position.width);
        minBottom = Math.min(minBottom, ny + el.position.height);
      }
      let corrX = 0,
        corrY = 0;
      if (minRight < 0) corrX = -minRight;
      else if (maxX > SLIDE_PAGE_WIDTH) corrX = SLIDE_PAGE_WIDTH - maxX;
      if (minBottom < 0) corrY = -minBottom;
      else if (maxY > SLIDE_PAGE_HEIGHT) corrY = SLIDE_PAGE_HEIGHT - maxY;

      const updates = items.map(({ id, el }) => ({
        id,
        updates: {
          position: {
            ...el.position,
            x: Math.round(el.position.x + dx + corrX),
            y: Math.round(el.position.y + dy + corrY),
          },
        },
      }));

      if (onUpdateMany) {
        onUpdateMany(updates);
      } else {
        updates.forEach(({ id, updates: patch }) => onUpdate(id, patch));
      }
    },
    [getElement, onUpdate, onUpdateMany]
  );

  /** Call from a keydown listener. Returns true if the event was handled. */
  const handleArrowKey = useCallback(
    (e: KeyboardEvent, selectedIds: string[]): boolean => {
      if (selectedIds.length === 0) return false;
      const step = e.shiftKey ? 10 : 1;
      switch (e.key) {
        case 'ArrowUp':
          nudge(selectedIds, 0, -step);
          break;
        case 'ArrowDown':
          nudge(selectedIds, 0, step);
          break;
        case 'ArrowLeft':
          nudge(selectedIds, -step, 0);
          break;
        case 'ArrowRight':
          nudge(selectedIds, step, 0);
          break;
        default:
          return false;
      }
      e.preventDefault();
      return true;
    },
    [nudge]
  );

  return { getHandlers, handleArrowKey };
}
